<template>
  <b>{{ $t('MANAGE.CONFIG.REPLACE_USER.TITLE') }}</b>
  <v-card class="mt-2 pa-6">
    <div>{{ $t('MANAGE.CONFIG.REPLACE_USER.DESC') }}</div>
    <div class="pt-3">
      <v-autocomplete v-model="selectedProjects"
                      :items="projects" :item-title="(project) => project.meta && project.meta.title || $t('PLANNING.UNNAMED_PROJECT')" item-value="id"
                      :label="$t('MANAGE.CONFIG.REPLACE_USER.PROJECTS_TO_REPLACE')" :placeholder="selectedProjects.length ? '' : $t('MULTIPROJECTS.NO_PROJECT_SELECTED')"
                      prepend-inner-icon="far fa-folder-open" multiple :menu-props="{ location: 'bottom' }"
                      @focus.once="loadPlanningsContent">
        <template v-if="! loadingPlanningContent" #prepend-item>
          <filter-toggle :selected-ids="selectedProjects" :sublist-ids="projectIds"
                         @update="selectedProjects = $event">
            {{ $t('MULTIPROJECTS.ALL_PROJECTS') }} ({{ projectIds.length }})
          </filter-toggle>
          <v-divider></v-divider>
        </template>
        <template #selection="{ item: { raw: item }, index }">
          <span v-if="selectedProjects.length == 1" class="mr-2">{{ item.meta && item.meta.title || $t('PLANNING.UNNAMED_PROJECT') }}</span>
          <span v-else-if="selectedProjects.length == projects.length && index === 0" class="mr-2">{{ $t('MULTIPROJECTS.ALL_PROJECTS') }} ({{ projects.length }})</span>
          <span v-else-if="index === 0" class="mr-2">{{ selectedProjects.length }}/{{ projects.length }}</span>
        </template>
        <template #no-data>
          <div v-if="loadingPlanningContent" class="text-center mt-2" style="height: 40px"><i class="far fa-spinner fa-spin fa-2x fa-fw"></i></div>
        </template>
      </v-autocomplete>
      <div class="my-4 d-flex align-center">
        <v-select v-model="replace.type" :items="replaceOptions.slice(0, -1)" :label="$t('MANAGE.CONFIG.REPLACE_USER.REPLACE')" prepend-inner-icon="far fa-user"
                  class="mr-4" style="max-width: 220px">
        </v-select>
        <v-select v-if="replace.type == 'username'" v-model="replace.username" :items="selectedPlanningsUsers.virtuals"
                  :disabled="! selectedProjects.length" item-title="username" :menu-props="{ location: 'bottom' }">
        </v-select>
        <v-select v-else-if="replace.type == 'user'" v-model="replace.user" :items="selectedPlanningsUsers.users" :item-title="$filters.username" item-value="id"
                  :disabled="! selectedProjects.length" :menu-props="{ location: 'bottom' }">
        </v-select>
        <v-select v-else-if="replace.type == 'group'" v-model="replace.group" :items="selectedPlanningsUsers.groups" item-title="title" item-value="group_id"
                  :disabled="! selectedProjects.length" :menu-props="{ location: 'bottom' }">
        </v-select>
      </div>
      <div class="my-4 d-flex align-center">
        <v-select v-model="replaceBy.type" :items="replaceOptions" :label="$t('MANAGE.CONFIG.REPLACE_USER.BY')" prepend-inner-icon="far fa-arrow-right"
                  class="mr-4" style="max-width: 220px">
        </v-select>
        <v-text-field v-if="replaceBy.type == 'username'" v-model="replaceBy.username" :disabled="! selectedProjects.length" autocomplete="nope">
        </v-text-field>
        <v-select v-else-if="replaceBy.type == 'user'" v-model="replaceBy.user" :items="organizationUsers" :item-title="$filters.username" item-value="id"
                  :disabled="! selectedProjects.length" :menu-props="{ location: 'bottom' }">
        </v-select>
        <v-select v-else-if="replaceBy.type == 'group'" v-model="replaceBy.group" :items="companyGroups" item-title="title" item-value="id"
                  :disabled="! selectedProjects.length" :menu-props="{ location: 'bottom' }">
        </v-select>
      </div>
      <v-checkbox v-model="disclaimer" class="mt-6">
        <template #label>
          <span>
            {{ $t('MANAGE.CONFIG.REPLACE_USER.DISCLAIMER') }}
            <b>{{ $t('MANAGE.CONFIG.REPLACE_USER.DISCLAIMER2') }}</b>
          </span>
        </template>
      </v-checkbox>
      <v-btn :disabled="! btnEnabled" :loading="massReplaceInProgress" rounded size="large" color="primary" block class="mt-2" @click="massReplaceUsers">
        {{ $t('MANAGE.CONFIG.REPLACE_USER.REPLACE_IN_PROJECTS', selectedProjects.length) }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
  import useUiMsgboxStore from '@/stores/ui/msgbox';
  import useUsersGroupsStore from '@/stores/users/groups';
  import useUsersStore from '@/stores/users';
  import useMultiprojectsPlanningsStore from '@/stores/multiprojects/plannings';

  export default {
    data() {
      return {
        loadingPlanningContent: false,
        projects: [],
        selectedProjects: [],
        replace: {
          type: 'username',
          user: null,
          group: null,
          username: '',
        },
        replaceBy: {
          type: 'user',
          user: null,
          group: null,
          username: '',
        },
        disclaimer: false,
        massReplaceInProgress: false,
        uiMsgboxStore: useUiMsgboxStore(),
        multiprojectsPlanningsStore: useMultiprojectsPlanningsStore(),
      };
    },
    computed: {
      replaceOptions() {
        return [
          { value: 'username', title: this.$t('MANAGE.CONFIG.REPLACE_USER.VIRTUAL_PARTICIPANT') },
          { value: 'user', title: this.$t('MANAGE.CONFIG.REPLACE_USER.REGISTERED_USER') },
          { value: 'group', title: this.$t('GLOBAL.GROUP') },
          { value: 'remove', title: this.$t('MANAGE.CONFIG.REPLACE_USER.REMOVE_USER') },
        ];
      },
      organizationUsers() {
        return useUsersStore().getOrganizationUsers;
      },
      companyGroups() {
        return useUsersGroupsStore().getCompanyGroups;
      },
      btnEnabled() {
        return this.disclaimer && this.selectedProjects.length && this.replace[this.replace.type] && (this.replaceBy.type == 'remove' || this.replaceBy[this.replaceBy.type]);
      },
      projectIds() {
        return this.projects.map(item => item.id);
      },
      planningUsers() {
        return this.projects.reduce((acc, planning) => {
          if (! planning || ! planning.elements) return acc;
          acc[planning.id] = planning.elements.reduce((planningAcc, el) => {
            if (el.getUsers()) planningAcc.push(...el.getUsers());
            return planningAcc;
          }, []);
          return acc;
        }, {});
      },
      selectedPlanningsUsers() {
        const selectedPlanningsUsers = { users: {}, groups: {}, virtuals: {} };
        this.selectedProjects.forEach((projectId) => {
          (this.planningUsers[projectId] || []).forEach((user) => {
            if (user.id && ! selectedPlanningsUsers.users[user.id]) {
              selectedPlanningsUsers.users[user.id] = user;
              return;
            }
            if (user.group_id && ! selectedPlanningsUsers.groups[user.group_id]) {
              selectedPlanningsUsers.groups[user.group_id] = user;
              return;
            }
            if (user.username && ! selectedPlanningsUsers.virtuals[user.username]) {
              selectedPlanningsUsers.virtuals[user.username] = user;
            }
          });
        });
        return {
          users: Object.keys(selectedPlanningsUsers.users).map(item => selectedPlanningsUsers.users[item]).sort((a, b) => (this.$filters.username(a) < this.$filters.username(b) ? -1 : 1)),
          groups: Object.keys(selectedPlanningsUsers.groups).map(item => selectedPlanningsUsers.groups[item]).sort((a, b) => (a.title?.toLowerCase() < b.title?.toLowerCase() ? -1 : 1)),
          virtuals: Object.keys(selectedPlanningsUsers.virtuals).map(item => selectedPlanningsUsers.virtuals[item]).sort((a, b) => (a.username.toLowerCase() < b.username.toLowerCase() ? -1 : 1)),
        };
      },
    },
    methods: {
      loadPlanningsContent() {
        this.loadingPlanningContent = true;
        this.multiprojectsPlanningsStore.loadPlanningsQuery({ users: true, access_right: 'admin' }).then((plannings) => {
          this.projects = plannings.sort((a, b) => {
            if (a?.meta && b?.meta) return (a.meta.title || "").toLowerCase() < (b.meta.title || "").toLowerCase() ? -1 : 1;
            return a?.meta ? -1 : 1;
          });
          this.loadingPlanningContent = false;
        });
      },
      massReplaceUsers() {
        if (! this.btnEnabled) return; // anti html hack
        this.massReplaceInProgress = true;
        window.apiSrv.call('plannings/tasks/massreplaceusers', 'store', {
          planning_ids: this.selectedProjects,
          replace_type: this.replace.type,
          replace_user_id: this.replace[this.replace.type],
          replace_by_type: this.replaceBy.type,
          replace_by_user_id: this.replaceBy[this.replaceBy.type],
        }).then((response) => {
          this.uiMsgboxStore.open({ title: this.$t('MANAGE.CONFIG.REPLACE_USER.REPLACEMENT_SUCCESSFUL'), body: response?.data?.message || "" });
          this.replace.user = null;
          this.replace.group = null;
          this.replace.username = '';
          this.loadPlanningsContent();
        }).catch((message) => {
          if (message) this.uiMsgboxStore.open({ title: this.$t('MANAGE.CONFIG.REPLACE_USER.ERROR_NOT_MODIFIED'), body: message || "" });
        }).then(() => {
          this.massReplaceInProgress = false;
        });
      },
    },
  };
</script>
